/**
 * UI State Redux slice
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface Modal {
  isOpen: boolean;
  type: string | null;
  data?: unknown;
}

interface UIState {
  isSidebarOpen: boolean;
  isMobileMenuOpen: boolean;
  isSearchOpen: boolean;
  showLoginPrompt: boolean;
  isLoggingOut: boolean;
  toasts: Toast[];
  modal: Modal;
  isPageLoading: boolean;
  searchQuery: string;
}

const initialState: UIState = {
  isSidebarOpen: false,
  isMobileMenuOpen: false,
  isSearchOpen: false,
  showLoginPrompt: false,
  isLoggingOut: false,
  toasts: [],
  modal: {
    isOpen: false,
    type: null,
    data: undefined,
  },
  isPageLoading: false,
  searchQuery: '',
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    /**
     * Toggle sidebar
     */
    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },

    /**
     * Set sidebar state
     */
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },

    /**
     * Toggle mobile menu
     */
    toggleMobileMenu: (state) => {
      state.isMobileMenuOpen = !state.isMobileMenuOpen;
    },

    /**
     * Set mobile menu state
     */
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.isMobileMenuOpen = action.payload;
    },

    /**
     * Toggle search
     */
    toggleSearch: (state) => {
      state.isSearchOpen = !state.isSearchOpen;
    },

    /**
     * Set search state
     */
    setSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.isSearchOpen = action.payload;
    },

    /**
     * Set search query
     */
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    /**
     * Clear search query
     */
    clearSearchQuery: (state) => {
      state.searchQuery = '';
    },

    /**
     * Add toast
     */
    addToast: (state, action: PayloadAction<Omit<Toast, 'id'>>) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      state.toasts.push({
        id,
        ...action.payload,
      });
    },

    /**
     * Remove toast
     */
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },

    /**
     * Clear all toasts
     */
    clearToasts: (state) => {
      state.toasts = [];
    },

    /**
     * Open modal
     */
    openModal: (state, action: PayloadAction<{ type: string; data?: unknown }>) => {
      state.modal = {
        isOpen: true,
        type: action.payload.type,
        data: action.payload.data,
      };
    },

    /**
     * Close modal
     */
    closeModal: (state) => {
      state.modal = {
        isOpen: false,
        type: null,
        data: undefined,
      };
    },

    /**
     * Set page loading state
     */
    setPageLoading: (state, action: PayloadAction<boolean>) => {
      state.isPageLoading = action.payload;
    },

    openLoginPrompt: (state) => {
      state.showLoginPrompt = true;
    },

    closeLoginPrompt: (state) => {
      state.showLoginPrompt = false;
    },

    setLoggingOut: (state, action: PayloadAction<boolean>) => {
      state.isLoggingOut = action.payload;
    },
  },
});

// Export actions
export const {
  toggleSidebar,
  setSidebarOpen,
  toggleMobileMenu,
  setMobileMenuOpen,
  toggleSearch,
  setSearchOpen,
  setSearchQuery,
  clearSearchQuery,
  addToast,
  removeToast,
  clearToasts,
  openModal,
  closeModal,
  setPageLoading,
  openLoginPrompt,
  closeLoginPrompt,
  setLoggingOut,
} = uiSlice.actions;

// Selectors
export const selectIsSidebarOpen = (state: { ui: UIState }) => state.ui.isSidebarOpen;
export const selectIsMobileMenuOpen = (state: { ui: UIState }) => state.ui.isMobileMenuOpen;
export const selectIsSearchOpen = (state: { ui: UIState }) => state.ui.isSearchOpen;
export const selectSearchQuery = (state: { ui: UIState }) => state.ui.searchQuery;
export const selectToasts = (state: { ui: UIState }) => state.ui.toasts;
export const selectModal = (state: { ui: UIState }) => state.ui.modal;
export const selectIsPageLoading = (state: { ui: UIState }) => state.ui.isPageLoading;
export const selectShowLoginPrompt = (state: { ui: UIState }) => state.ui.showLoginPrompt;
export const selectIsLoggingOut = (state: { ui: UIState }) => state.ui.isLoggingOut;

export default uiSlice.reducer;
